import { useQuery, useSubscription } from "urql";
import { graphql } from "../../../../../__generated__/gql";
import Modal from "../../../../../ui/Modal/Modal";
import Tabs from "../../../../../ui/Tabs/Tabs";
import Details from "./Details";
import styles from "./EditArticleModal.module.scss";
import Loader from "../../../../../ui/Loader/Loader";
import ContentBriefForm from "./ContentBriefForm";
import Outline from "./Outline";
import { useState } from "react";

const EditArticleModalQuery = graphql(/* GraphQL */ `
  query editArticleModal($id: String!) {
    article(id: $id) {
      ...EditArticleDetails
      ...EditArticleBrief
      ...EditArticleOutline
    }
    creditsRequired(input: { filters: [{ ids: [$id] }] })
  }
`);

const EditArticleModalSubscription = graphql(/* GraphQL */ `
  subscription editArticleModalSubscription {
    articleUpdated {
      ...EditArticleDetails
      ...EditArticleBrief
      ...EditArticleOutline
    }
  }
`);

function InnerEditArticleModal({
  id,
  onClose,
  isOpen,
}: {
  isOpen: boolean;
  onClose: () => void;
  id: string;
}) {
  const [isDirty, setIsDirty] = useState(false);
  const [editArticleModalResponse] = useQuery({
    query: EditArticleModalQuery,
    variables: { id },
    requestPolicy: "network-only",
  });

  useSubscription({ query: EditArticleModalSubscription });

  if (
    editArticleModalResponse.fetching ||
    !editArticleModalResponse.data?.article
  ) {
    return (
      <div className={styles.loader}>
        <Loader />
      </div>
    );
  }

  return (
    <Tabs
      blockIfDirty
      isDirty={isDirty}
      classNames={{ list: styles.tabs, content: styles.tabContent }}
      defaultValue="details"
      tabs={[
        {
          id: "details",
          label: "Details",
          content: (
            <Details
              onClose={onClose}
              id={id}
              isOpen={isOpen}
              onChange={setIsDirty}
              data={editArticleModalResponse.data?.article}
            />
          ),
        },
        {
          id: "content",
          label: "Content brief",
          content: (
            <ContentBriefForm
              data={editArticleModalResponse.data?.article}
              className={styles.contentBriefForm}
              isOpen={isOpen}
              onClose={onClose}
              onChange={setIsDirty}
              creditsRequired={editArticleModalResponse.data?.creditsRequired}
            />
          ),
        },
        {
          id: "outline",
          label: "Outline",
          content: (
            <Outline
              onClose={onClose}
              onChange={setIsDirty}
              isOpen={isOpen}
              data={editArticleModalResponse.data?.article}
              creditsRequired={editArticleModalResponse.data?.creditsRequired}
            />
          ),
        },
      ]}
    />
  );
}

function EditArticleModal({
  isOpen,
  onClose,
  id,
}: {
  isOpen: boolean;
  onClose: () => void;
  id: string;
}) {
  return (
    <Modal
      fullHeight
      stickyHeader
      isOpen={isOpen}
      onClose={onClose}
      title="Article details"
      size="large"
      classNames={{ body: styles.modalBody }}
    >
      <InnerEditArticleModal id={id} onClose={onClose} isOpen={isOpen} />
    </Modal>
  );
}

export default EditArticleModal;
